import React, { useEffect, type ReactNode, useState } from "react";
import authService, {
  type AuthUser,
  type ChangePasswordResponse,
  type LoginResult,
  type RegisterOrganizationRequest,
  type RegisterOrganizationResponse,
} from "@/services/authService";
import { AuthContext, type AuthContextType } from "@/contexts/AuthContextInstance";
import { getApiBaseUrl } from "@/config/apiBaseUrl";

const API_BASE_URL = getApiBaseUrl();

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Потеря сессии может прийти из перехватчика API, а не только из React-дерева,
  // поэтому реакцию регистрируем один раз на весь жизненный цикл провайдера
  useEffect(() => {
    authService.setSessionExpiredHandler(() => {
      setUser(null);
      setIsAuthenticated(false);
    });
    return () => authService.setSessionExpiredHandler(null);
  }, []);

  // Фоновое продление токена держим ровно столько, сколько живёт сессия
  useEffect(() => {
    if (!isAuthenticated) {
      authService.stopAutoRefresh();
      return;
    }
    authService.startAutoRefresh();
    return () => authService.stopAutoRefresh();
  }, [isAuthenticated]);

  // Проверка аутентификации при загрузке приложения
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);

      try {
        const savedUser = authService.getUser();
        const hasStoredSession = Boolean(authService.getToken()) && savedUser !== null;

        // Токен на руках есть, но по локальным часам он протух — пробуем продлить,
        // иначе перезагрузка страницы через час всегда выкидывала на форму входа
        const sessionUsable = hasStoredSession && (authService.isAuthenticated() || (await authService.refresh()));

        if (sessionUsable && savedUser) {
          // Проверяем валидность токена на сервере
          const response = await fetch(`${API_BASE_URL}/v2/auth/validate-token`, {
            method: "POST",
            headers: {
              ...authService.getAuthHeaders(),
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const data = (await response.json()) as { valid?: boolean; user?: AuthUser };
            if (data.valid && data.user) {
              setUser(data.user);
              setIsAuthenticated(true);
            } else {
              // Токен невалиден, очищаем локальные данные
              authService.logout();
              setUser(null);
              setIsAuthenticated(false);
            }
          } else {
            // Ошибка проверки токена
            authService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else if (hasStoredSession) {
          // Сервер отказался продлевать сессию — восстанавливать нечего
          authService.logout();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth check error:", error);
        // Сеть недоступна: с живым токеном оставляем пользователя внутри,
        // но протухший токен так «спасать» нельзя — все запросы всё равно дадут 401
        const fallbackUser = authService.getUser();
        if (fallbackUser && authService.isAuthenticated()) {
          setUser(fallbackUser);
          setIsAuthenticated(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const loginWithResult = async (email: string, password: string, totpCode: string): Promise<LoginResult> => {
    try {
      const result = await authService.login(email, password, totpCode);

      if (result.success) {
        const loggedUser = authService.getUser();
        setUser(loggedUser);
        setIsAuthenticated(true);
      }

      return result;
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        requiresTotp: false,
        requiresPasswordChange: false,
        error: "Произошла ошибка при входе в систему",
      };
    }
  };

  const login = async (email: string, password: string, totpCode: string): Promise<boolean> => {
    const result = await loginWithResult(email, password, totpCode);
    return result.success;
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<ChangePasswordResponse> => {
    return authService.changePassword(currentPassword, newPassword, confirmPassword);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const register = async (data: RegisterOrganizationRequest): Promise<RegisterOrganizationResponse> => {
    return authService.register(data);
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    loginWithResult,
    changePassword,
    register,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
